import {
  BOOK_ISBN,
  BOOK_METADATA,
  PURCHASE_LINKS,
  PURCHASE_PRICES,
  absoluteUrl,
} from "~/lib/book-purchase";

export function useHomeSeo() {
  const { t, locale } = useI18n();
  const siteUrl = "https://aartedainfluencia.com.br";
  const bookTitle = t("book-title");
  const description = t("home.description");
  const ogImage = absoluteUrl(BOOK_METADATA.coverImagePath, siteUrl);

  useSeoMeta({
    title: t("home.title"),
    description,
    author: BOOK_METADATA.author,
    ogTitle: t("home.og-title"),
    ogDescription: t("home.og-description"),
    ogImage,
    ogImageAlt: bookTitle,
    ogType: "website",
    ogLocale: locale.value === "pt-BR" ? "pt_BR" : locale.value.replace("-", "_"),
    twitterCard: "summary_large_image",
    twitterTitle: t("home.og-title"),
    twitterDescription: t("home.og-description"),
    twitterImage: ogImage,
    keywords: t("home.keywords"),
  });

  const faqItems = computed(() =>
    [1, 2, 3, 4].map((index) => ({
      question: t(`faq.question-${index}`, { book: bookTitle, livro: bookTitle }),
      answer: t(`faq.answer-${index}`, { book: bookTitle }),
    })),
  );

  useSchemaOrg([
    definePerson({
      name: BOOK_METADATA.author,
      url: siteUrl,
      jobTitle: t("seo.author-job-title"),
      sameAs: ["https://www.instagram.com/aartedainfluencia/"],
    }),
    defineBook({
      name: bookTitle,
      alternateName: "A Arte da Influência",
      author: { "@type": "Person", name: BOOK_METADATA.author },
      isbn: BOOK_ISBN,
      bookEdition: BOOK_METADATA.edition,
      datePublished: BOOK_METADATA.yearPublished,
      numberOfPages: BOOK_METADATA.numberOfPages,
      inLanguage: locale.value,
      description,
      image: ogImage,
      url: siteUrl,
      offers: buildBookOffers(locale.value),
    }),
    {
      "@type": "FAQPage",
      mainEntity: faqItems.value.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    },
  ]);
}

function buildBookOffers(locale: string) {
  const livrariasOffer = defineOffer({
    name: "Livrarias Curitiba",
    url: PURCHASE_LINKS.livrariasCuritiba,
    price: PURCHASE_PRICES.livrariasCuritiba.amount,
    priceCurrency: PURCHASE_PRICES.livrariasCuritiba.currency,
    availability: "https://schema.org/InStock",
    itemCondition: "https://schema.org/NewCondition",
    seller: defineOrganization({ name: "Livrarias Curitiba" }),
  });

  const offers = [
    livrariasOffer,
    defineOffer({
      name: "Amazon Internacional",
      url: PURCHASE_LINKS.amazonInternational,
      price: PURCHASE_PRICES.amazonInternational.amount,
      priceCurrency: PURCHASE_PRICES.amazonInternational.currency,
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: defineOrganization({ name: "Amazon" }),
    }),
  ];

  if (locale === "pt-BR") {
    offers.push(
      defineOffer({
        name: "Livro impresso",
        url: PURCHASE_LINKS.printedCheckout,
        price: PURCHASE_PRICES.printedCheckout.amount,
        priceCurrency: PURCHASE_PRICES.printedCheckout.currency,
        availability: "https://schema.org/InStock",
        itemCondition: "https://schema.org/NewCondition",
      }),
      defineOffer({
        name: "E-book",
        url: PURCHASE_LINKS.ebookCheckout,
        price: PURCHASE_PRICES.ebookCheckout.amount,
        priceCurrency: PURCHASE_PRICES.ebookCheckout.currency,
        availability: "https://schema.org/InStock",
        itemCondition: "https://schema.org/NewCondition",
      }),
      defineOffer({
        name: "Amazon Brasil",
        url: PURCHASE_LINKS.amazonBrazil,
        availability: "https://schema.org/InStock",
        itemCondition: "https://schema.org/NewCondition",
        seller: defineOrganization({ name: "Amazon Brasil" }),
      }),
    );
  }

  return offers;
}
